import React from 'react';
import { Box, Typography, Paper, Grid, Alert, Divider, List, ListItem, ListItemIcon, ListItemText, Chip, Table, TableBody, TableCell, TableHead, TableRow } from '@mui/material';
import {
  CheckCircle as CheckIcon,
  Settings as SettingsIcon,
  People as PeopleIcon,
  PersonOff as OfflineIcon,
  Lock as LockIcon,
  Shuffle as ShuffleIcon,
  ContentCopy as CopyIcon,
  Edit as EditIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  SupervisorAccount as ManagerIcon,
  HourglassEmpty as PendingIcon,
  History as AuditIcon,
  Assignment as RequestIcon,
  MergeType as MergeIcon,
  Send as SendIcon,
  Hub as ConnectionsIcon,
  TouchApp as AssignIcon,
} from '@mui/icons-material';
import { SupportLayout, InfoCard, VisualExample, MiniGrid } from '../../components/support';
import { useDocumentTitle } from '../../hooks/core';

export const BoardManagementPage: React.FC = () => {
  useDocumentTitle('Board Management - Support');

  return (
    <SupportLayout
      title="Board Management"
      description="Learn how to create, configure, and manage your sports squares boards effectively."
      breadcrumbs={[{ label: 'Board Management' }]}
    >
      {/* Board Status Section */}
      <Box id="board-status" sx={{ mb: 5 }}>
        <Typography variant="h5" gutterBottom fontWeight="bold">
          Board Status Explained
        </Typography>
        <Typography variant="body1" paragraph>
          Every board goes through four statuses during its lifecycle. Understanding these helps you manage your pool effectively.
        </Typography>

        <Grid container spacing={3}>
          {[
            {
              status: 'Draft',
              color: 'grey.500',
              description: 'Initial setup phase. Configure settings, payouts, and prepare your board.',
              canDo: ['Edit all settings', 'Configure payouts', 'Set price per square'],
              cantDo: ['Accept players', 'Record scores'],
            },
            {
              status: 'Live',
              color: 'success.main',
              description: 'Board is open for players. Share your invite code and start claiming squares.',
              canDo: ['Players can join', 'Claim/assign squares', 'Add offline players', 'Randomize numbers'],
              cantDo: ['Record scores (until locked)'],
            },
            {
              status: 'Locked',
              color: 'warning.main',
              description: 'Game day! No more changes to squares allowed. Ready for score recording.',
              canDo: ['Record quarter scores', 'View winners', 'Manage payments'],
              cantDo: ['Change square ownership', 'Add new players'],
            },
            {
              status: 'Completed',
              color: 'info.main',
              description: 'All scores recorded. Board is archived for reference and accounting.',
              canDo: ['View results', 'Manage payments', 'Export board'],
              cantDo: ['Change scores', 'Modify squares'],
            },
          ].map((item) => (
            <Grid size={{ xs: 12, sm: 6 }} key={item.status}>
              <Paper elevation={0} sx={{ p: 3, border: 2, borderColor: item.color, height: '100%' }}>
                <Chip
                  label={item.status}
                  sx={{ bgcolor: item.color, color: 'white', fontWeight: 'bold', mb: 2 }}
                />
                <Typography variant="body2" paragraph>
                  {item.description}
                </Typography>
                <Typography variant="subtitle2" color="success.main" sx={{ mb: 1 }}>
                  You can:
                </Typography>
                <List dense disablePadding>
                  {item.canDo.map((action) => (
                    <ListItem key={action} disablePadding sx={{ py: 0.25 }}>
                      <ListItemIcon sx={{ minWidth: 28 }}>
                        <CheckIcon fontSize="small" color="success" />
                      </ListItemIcon>
                      <ListItemText primary={action} primaryTypographyProps={{ variant: 'body2' }} />
                    </ListItem>
                  ))}
                </List>
                {item.cantDo.length > 0 && (
                  <>
                    <Typography variant="subtitle2" color="error.main" sx={{ mt: 1, mb: 1 }}>
                      You cannot:
                    </Typography>
                    <List dense disablePadding>
                      {item.cantDo.map((action) => (
                        <ListItem key={action} disablePadding sx={{ py: 0.25 }}>
                          <ListItemText
                            primary={action}
                            primaryTypographyProps={{ variant: 'body2', color: 'text.secondary' }}
                          />
                        </ListItem>
                      ))}
                    </List>
                  </>
                )}
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Box>

      <Divider sx={{ my: 4 }} />

      {/* Creating a Board */}
      <Box id="creating-board" sx={{ mb: 5 }}>
        <Typography variant="h5" gutterBottom fontWeight="bold">
          <SettingsIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
          Creating a New Board
        </Typography>

        <Typography variant="body1" paragraph>
          When creating a board, you'll configure the following settings:
        </Typography>

        <Paper elevation={0} sx={{ border: 1, borderColor: 'divider', overflow: 'hidden' }}>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: 'grey.100' }}>
                <TableCell sx={{ fontWeight: 'bold' }}>Setting</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Description</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Example</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                <TableCell><strong>Board Name</strong></TableCell>
                <TableCell>A descriptive name for your pool</TableCell>
                <TableCell>Super Bowl 2025 Pool</TableCell>
              </TableRow>
              <TableRow>
                <TableCell><strong>Slug (Optional)</strong></TableCell>
                <TableCell>URL-friendly identifier for sharing</TableCell>
                <TableCell>superbowl-2025</TableCell>
              </TableRow>
              <TableRow>
                <TableCell><strong>Sport</strong></TableCell>
                <TableCell>Type of sport (affects terminology)</TableCell>
                <TableCell>Football, Basketball, Hockey</TableCell>
              </TableRow>
              <TableRow>
                <TableCell><strong>Teams</strong></TableCell>
                <TableCell>Home and Away team names</TableCell>
                <TableCell>Chiefs vs Eagles</TableCell>
              </TableRow>
              <TableRow>
                <TableCell><strong>Team Colors</strong></TableCell>
                <TableCell>Custom colors for visual distinction</TableCell>
                <TableCell>#E31837, #004C54</TableCell>
              </TableRow>
              <TableRow>
                <TableCell><strong>Game Date</strong></TableCell>
                <TableCell>When the game takes place</TableCell>
                <TableCell>Feb 9, 2025 at 6:30 PM</TableCell>
              </TableRow>
              <TableRow>
                <TableCell><strong>Price Per Square</strong></TableCell>
                <TableCell>Cost for each square on the board</TableCell>
                <TableCell>$10 (= $1,000 total pot)</TableCell>
              </TableRow>
              <TableRow>
                <TableCell><strong>Quarters</strong></TableCell>
                <TableCell>Number of scoring periods (2-4)</TableCell>
                <TableCell>4 (Q1, Q2, Q3, Final)</TableCell>
              </TableRow>
              <TableRow>
                <TableCell><strong>Number Sets</strong></TableCell>
                <TableCell>Use single, half, or quarter mode for numbers</TableCell>
                <TableCell>Single (same all game) or Half/Quarter (different per period)</TableCell>
              </TableRow>
              <TableRow>
                <TableCell><strong>Description</strong></TableCell>
                <TableCell>Payment instructions or rules</TableCell>
                <TableCell>Venmo: @john-doe</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </Paper>

        <Alert severity="info" sx={{ mt: 2 }}>
          <strong>Pro Tip:</strong> Use templates to quickly create boards with your favorite settings. See the Templates section below.
        </Alert>
      </Box>

      <Divider sx={{ my: 4 }} />

      {/* Board Templates */}
      <Box id="templates" sx={{ mb: 5 }}>
        <Typography variant="h5" gutterBottom fontWeight="bold">
          <CopyIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
          Board Templates
        </Typography>

        <Typography variant="body1" paragraph>
          Templates save your preferred board configuration so you can quickly create new boards without re-entering all the settings. Create templates for different sports, events, or payout structures.
        </Typography>

        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid size={{ xs: 12, md: 6 }}>
            <Paper elevation={0} sx={{ p: 3, border: 1, borderColor: 'divider', height: '100%' }}>
              <Typography variant="h6" gutterBottom>What Templates Save</Typography>
              <List dense>
                <ListItem>
                  <ListItemIcon><CheckIcon color="success" /></ListItemIcon>
                  <ListItemText primary="Sport type" secondary="Football, Basketball, Hockey, etc." />
                </ListItem>
                <ListItem>
                  <ListItemIcon><CheckIcon color="success" /></ListItemIcon>
                  <ListItemText primary="Number of quarters" secondary="2, 3, or 4 scoring periods" />
                </ListItem>
                <ListItem>
                  <ListItemIcon><CheckIcon color="success" /></ListItemIcon>
                  <ListItemText primary="Number mode" secondary="Single, Half, or Quarter number sets" />
                </ListItem>
                <ListItem>
                  <ListItemIcon><CheckIcon color="success" /></ListItemIcon>
                  <ListItemText primary="Price per square" secondary="Your default square price" />
                </ListItem>
                <ListItem>
                  <ListItemIcon><CheckIcon color="success" /></ListItemIcon>
                  <ListItemText primary="Payout configuration" secondary="All payout rules and amounts" />
                </ListItem>
                <ListItem>
                  <ListItemIcon><CheckIcon color="success" /></ListItemIcon>
                  <ListItemText primary="Theme selection" secondary="Your preferred board theme" />
                </ListItem>
              </List>
            </Paper>
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <Paper elevation={0} sx={{ p: 3, border: 1, borderColor: 'divider', height: '100%' }}>
              <Typography variant="h6" gutterBottom>Managing Templates</Typography>
              <List dense>
                <ListItem>
                  <ListItemIcon><CheckIcon color="success" /></ListItemIcon>
                  <ListItemText primary="Create from Profile > Templates" />
                </ListItem>
                <ListItem>
                  <ListItemIcon><CheckIcon color="success" /></ListItemIcon>
                  <ListItemText primary="Edit or delete existing templates" />
                </ListItem>
                <ListItem>
                  <ListItemIcon><CheckIcon color="success" /></ListItemIcon>
                  <ListItemText primary="Set a default template" secondary="Auto-selected when creating boards" />
                </ListItem>
                <ListItem>
                  <ListItemIcon><CheckIcon color="success" /></ListItemIcon>
                  <ListItemText primary="Import/Export templates" secondary="Share templates or back them up" />
                </ListItem>
              </List>
            </Paper>
          </Grid>
        </Grid>

        <InfoCard title="Creating a Template from a Board" variant="tip" icon={<CopyIcon />}>
          <Typography variant="body2">
            After configuring a board exactly how you like it, you can save it as a template for future use. This is the easiest way to capture complex payout configurations.
          </Typography>
        </InfoCard>
      </Box>

      <Divider sx={{ my: 4 }} />

      {/* Managing Players */}
      <Box id="managing-players" sx={{ mb: 5 }}>
        <Typography variant="h5" gutterBottom fontWeight="bold">
          <PeopleIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
          Managing Players
        </Typography>

        <Grid container spacing={3}>
          <Grid size={{ xs: 12, md: 6 }}>
            <Paper elevation={0} sx={{ p: 3, border: 1, borderColor: 'divider', height: '100%' }}>
              <Typography variant="h6" gutterBottom>Invite Code</Typography>
              <Typography variant="body2" paragraph>
                Every board has a unique 6-character invite code. Players enter this code to join your board.
              </Typography>
              <Box sx={{ bgcolor: 'grey.100', p: 2, borderRadius: 1, textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary">Your code:</Typography>
                <Typography variant="h5" fontFamily="monospace" fontWeight="bold" letterSpacing={2}>
                  XYZ789
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                Share this code via text, email, or any messaging app.
              </Typography>
            </Paper>
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <Paper elevation={0} sx={{ p: 3, border: 1, borderColor: 'divider', height: '100%' }}>
              <Typography variant="h6" gutterBottom>Player Actions</Typography>
              <List dense>
                <ListItem>
                  <ListItemIcon><CheckIcon color="success" /></ListItemIcon>
                  <ListItemText
                    primary="View all participants"
                    secondary="See who has joined and their squares"
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon><CheckIcon color="success" /></ListItemIcon>
                  <ListItemText
                    primary="Assign squares"
                    secondary="Manually assign squares to players"
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon><CheckIcon color="success" /></ListItemIcon>
                  <ListItemText
                    primary="Clear squares"
                    secondary="Remove ownership from a square"
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon><CheckIcon color="success" /></ListItemIcon>
                  <ListItemText
                    primary="Add labels"
                    secondary="Custom names for display on squares"
                  />
                </ListItem>
              </List>
            </Paper>
          </Grid>
        </Grid>
      </Box>

      <Divider sx={{ my: 4 }} />

      {/* Player Connections */}
      <Box id="connections" sx={{ mb: 5 }}>
        <Typography variant="h5" gutterBottom fontWeight="bold">
          <ConnectionsIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
          Player Connections
        </Typography>

        <Typography variant="body1" paragraph>
          <strong>Connections</strong> remember players you work with frequently, making it faster to add them to new boards and manage recurring players.
        </Typography>

        <Grid container spacing={3}>
          <Grid size={{ xs: 12, md: 6 }}>
            <Paper elevation={0} sx={{ p: 3, border: 1, borderColor: 'divider', height: '100%' }}>
              <Typography variant="h6" gutterBottom>How Connections Are Created</Typography>
              <List dense>
                <ListItem>
                  <ListItemIcon><CheckIcon color="success" /></ListItemIcon>
                  <ListItemText
                    primary="Merge request approved"
                    secondary="Both parties become connections automatically"
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon><CheckIcon color="success" /></ListItemIcon>
                  <ListItemText
                    primary="Shared boards"
                    secondary="Players on 2+ of your boards become connections"
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon><CheckIcon color="success" /></ListItemIcon>
                  <ListItemText
                    primary="Manual addition"
                    secondary="Add connections from the Connections page"
                  />
                </ListItem>
              </List>
            </Paper>
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <Paper elevation={0} sx={{ p: 3, border: 1, borderColor: 'divider', height: '100%' }}>
              <Typography variant="h6" gutterBottom>Using Connections</Typography>
              <List dense>
                <ListItem>
                  <ListItemIcon><CheckIcon color="success" /></ListItemIcon>
                  <ListItemText
                    primary="Quick player addition"
                    secondary="Connections appear in the Add Player dialog"
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon><CheckIcon color="success" /></ListItemIcon>
                  <ListItemText
                    primary="Assign Mode dropdown"
                    secondary="Assign squares to connections not yet on the board"
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon><CheckIcon color="success" /></ListItemIcon>
                  <ListItemText
                    primary="Proactive merging"
                    secondary="Initiate merges from Connections page"
                  />
                </ListItem>
              </List>
            </Paper>
          </Grid>
        </Grid>

        <Paper elevation={0} sx={{ p: 3, border: 1, borderColor: 'primary.main', bgcolor: 'primary.50', mt: 3 }}>
          <Typography variant="h6" gutterBottom>Managing Connections</Typography>
          <Typography variant="body2" paragraph>
            From your Connections page, you can:
          </Typography>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Box sx={{ textAlign: 'center' }}>
                <Chip label="View" color="primary" size="small" sx={{ mb: 1 }} />
                <Typography variant="body2">See shared boards and history</Typography>
              </Box>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Box sx={{ textAlign: 'center' }}>
                <Chip label="Edit" color="secondary" size="small" sx={{ mb: 1 }} />
                <Typography variant="body2">Add nicknames and notes</Typography>
              </Box>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Box sx={{ textAlign: 'center' }}>
                <Chip label="Merge" color="info" size="small" sx={{ mb: 1 }} />
                <Typography variant="body2">Initiate offline player merges</Typography>
              </Box>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Box sx={{ textAlign: 'center' }}>
                <Chip label="Remove" color="error" size="small" sx={{ mb: 1 }} />
                <Typography variant="body2">Clean up old connections</Typography>
              </Box>
            </Grid>
          </Grid>
        </Paper>

        <Alert severity="info" sx={{ mt: 2 }}>
          <Typography variant="body2">
            <strong>Tip:</strong> When someone sends you a merge request, click the notification to review it on your Connections page. Pending requests appear at the top with a "Review" button.
          </Typography>
        </Alert>
      </Box>

      <Divider sx={{ my: 4 }} />

      {/* Enhanced Assign Mode */}
      <Box id="assign-mode" sx={{ mb: 5 }}>
        <Typography variant="h5" gutterBottom fontWeight="bold">
          <AssignIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
          Enhanced Assign Mode
        </Typography>

        <Typography variant="body1" paragraph>
          Assign Mode lets managers directly assign squares to players. The enhanced dropdown makes it easy to assign to any player type—not just those already on the board.
        </Typography>

        <Paper elevation={0} sx={{ p: 3, border: 1, borderColor: 'divider', mb: 3 }}>
          <Typography variant="h6" gutterBottom>Categorized Player Selection</Typography>
          <Typography variant="body2" paragraph>
            When you click a square in assign mode, the dropdown shows players grouped by type:
          </Typography>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold' }}>Category</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Description</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                <TableCell><Chip label="On Board" size="small" color="success" /></TableCell>
                <TableCell>Players already participating on this board</TableCell>
              </TableRow>
              <TableRow>
                <TableCell><Chip label="Connections" size="small" color="primary" /></TableCell>
                <TableCell>Your saved connections (not yet on this board)</TableCell>
              </TableRow>
              <TableRow>
                <TableCell><Chip label="Offline Players" size="small" color="secondary" /></TableCell>
                <TableCell>Your offline players from other boards</TableCell>
              </TableRow>
              <TableRow>
                <TableCell><Chip label="Pending Invites" size="small" color="warning" /></TableCell>
                <TableCell>Invited users who haven't joined yet</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </Paper>

        <Grid container spacing={3}>
          <Grid size={{ xs: 12, md: 6 }}>
            <Paper elevation={0} sx={{ p: 3, border: 1, borderColor: 'success.main', bgcolor: 'success.50', height: '100%' }}>
              <Typography variant="h6" gutterBottom>Square Reservations</Typography>
              <Typography variant="body2" paragraph>
                When you assign a square to someone not yet on the board, SquareUp <strong>reserves</strong> it for them:
              </Typography>
              <List dense disablePadding>
                <ListItem disablePadding sx={{ py: 0.25 }}>
                  <ListItemIcon sx={{ minWidth: 28 }}><CheckIcon fontSize="small" color="success" /></ListItemIcon>
                  <ListItemText primary="Square shows as reserved with a special indicator" primaryTypographyProps={{ variant: 'body2' }} />
                </ListItem>
                <ListItem disablePadding sx={{ py: 0.25 }}>
                  <ListItemIcon sx={{ minWidth: 28 }}><CheckIcon fontSize="small" color="success" /></ListItemIcon>
                  <ListItemText primary="When they join, squares are automatically assigned" primaryTypographyProps={{ variant: 'body2' }} />
                </ListItem>
                <ListItem disablePadding sx={{ py: 0.25 }}>
                  <ListItemIcon sx={{ minWidth: 28 }}><CheckIcon fontSize="small" color="success" /></ListItemIcon>
                  <ListItemText primary="If invite expires, reserved squares return to the pool" primaryTypographyProps={{ variant: 'body2' }} />
                </ListItem>
              </List>
            </Paper>
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <Paper elevation={0} sx={{ p: 3, border: 1, borderColor: 'info.main', bgcolor: 'info.50', height: '100%' }}>
              <Typography variant="h6" gutterBottom>Keyboard Navigation</Typography>
              <Typography variant="body2" paragraph>
                The assign dropdown supports full keyboard navigation:
              </Typography>
              <List dense disablePadding>
                <ListItem disablePadding sx={{ py: 0.25 }}>
                  <ListItemIcon sx={{ minWidth: 28 }}><CheckIcon fontSize="small" color="info" /></ListItemIcon>
                  <ListItemText primary="Arrow keys to move through players" primaryTypographyProps={{ variant: 'body2' }} />
                </ListItem>
                <ListItem disablePadding sx={{ py: 0.25 }}>
                  <ListItemIcon sx={{ minWidth: 28 }}><CheckIcon fontSize="small" color="info" /></ListItemIcon>
                  <ListItemText primary="Enter to select the highlighted player" primaryTypographyProps={{ variant: 'body2' }} />
                </ListItem>
                <ListItem disablePadding sx={{ py: 0.25 }}>
                  <ListItemIcon sx={{ minWidth: 28 }}><CheckIcon fontSize="small" color="info" /></ListItemIcon>
                  <ListItemText primary="Escape to close the dropdown" primaryTypographyProps={{ variant: 'body2' }} />
                </ListItem>
                <ListItem disablePadding sx={{ py: 0.25 }}>
                  <ListItemIcon sx={{ minWidth: 28 }}><CheckIcon fontSize="small" color="info" /></ListItemIcon>
                  <ListItemText primary="Type to filter players by name" primaryTypographyProps={{ variant: 'body2' }} />
                </ListItem>
              </List>
            </Paper>
          </Grid>
        </Grid>

        <Alert severity="success" sx={{ mt: 3 }}>
          <Typography variant="body2">
            <strong>Race Condition Prevention:</strong> Square reservations use atomic database operations to prevent duplicate assignments, even if two managers try to reserve the same square simultaneously.
          </Typography>
        </Alert>
      </Box>

      <Divider sx={{ my: 4 }} />

      {/* Offline Players */}
      <Box id="offline-players" sx={{ mb: 5 }}>
        <Typography variant="h5" gutterBottom fontWeight="bold">
          <OfflineIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
          Offline Players
        </Typography>

        <Typography variant="body1" paragraph>
          Not everyone wants to create an account. <strong>Offline players</strong> let you add people who prefer the traditional approach—you manage their squares for them.
        </Typography>

        <Grid container spacing={3}>
          <Grid size={{ xs: 12, md: 6 }}>
            <InfoCard title="When to Use Offline Players" variant="info">
              <List dense disablePadding>
                <ListItem disablePadding sx={{ py: 0.5 }}>
                  <ListItemText primary="Family members who aren't tech-savvy" />
                </ListItem>
                <ListItem disablePadding sx={{ py: 0.5 }}>
                  <ListItemText primary="Coworkers who want to play but not sign up" />
                </ListItem>
                <ListItem disablePadding sx={{ py: 0.5 }}>
                  <ListItemText primary="Cash players at in-person events" />
                </ListItem>
                <ListItem disablePadding sx={{ py: 0.5 }}>
                  <ListItemText primary="People who pay you directly (Venmo, cash)" />
                </ListItem>
              </List>
            </InfoCard>
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <Paper elevation={0} sx={{ p: 3, border: 1, borderColor: 'divider', height: '100%' }}>
              <Typography variant="h6" gutterBottom>Creating Offline Players</Typography>
              <List dense>
                <ListItem>
                  <ListItemIcon><CheckIcon color="success" /></ListItemIcon>
                  <ListItemText primary="Go to Profile > Offline Players" />
                </ListItem>
                <ListItem>
                  <ListItemIcon><CheckIcon color="success" /></ListItemIcon>
                  <ListItemText primary="Add name and optional icon" />
                </ListItem>
                <ListItem>
                  <ListItemIcon><CheckIcon color="success" /></ListItemIcon>
                  <ListItemText primary="Use them across all your boards" />
                </ListItem>
                <ListItem>
                  <ListItemIcon><CheckIcon color="success" /></ListItemIcon>
                  <ListItemText primary="Track their payments separately" />
                </ListItem>
              </List>
            </Paper>
          </Grid>
        </Grid>

        {/* Offline Player Advanced Features */}
        <Paper elevation={0} sx={{ p: 3, border: 1, borderColor: 'primary.main', bgcolor: 'primary.50', mt: 3 }}>
          <Typography variant="h6" gutterBottom>Advanced Offline Player Settings</Typography>
          <Grid container spacing={3}>
            <Grid size={{ xs: 12, md: 6 }}>
              <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                Approved Squares Limit
              </Typography>
              <Typography variant="body2" paragraph>
                Set a maximum number of squares an offline player can claim. This is useful when:
              </Typography>
              <List dense disablePadding>
                <ListItem disablePadding sx={{ py: 0.25 }}>
                  <ListItemIcon sx={{ minWidth: 28 }}><CheckIcon fontSize="small" color="primary" /></ListItemIcon>
                  <ListItemText primary="Limiting exposure for players" primaryTypographyProps={{ variant: 'body2' }} />
                </ListItem>
                <ListItem disablePadding sx={{ py: 0.25 }}>
                  <ListItemIcon sx={{ minWidth: 28 }}><CheckIcon fontSize="small" color="primary" /></ListItemIcon>
                  <ListItemText primary="Ensuring fair distribution" primaryTypographyProps={{ variant: 'body2' }} />
                </ListItem>
                <ListItem disablePadding sx={{ py: 0.25 }}>
                  <ListItemIcon sx={{ minWidth: 28 }}><CheckIcon fontSize="small" color="primary" /></ListItemIcon>
                  <ListItemText primary="Managing cash collection" primaryTypographyProps={{ variant: 'body2' }} />
                </ListItem>
              </List>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                Starting Balance
              </Typography>
              <Typography variant="body2" paragraph>
                Set an initial balance for offline players who have pre-paid. This is helpful when:
              </Typography>
              <List dense disablePadding>
                <ListItem disablePadding sx={{ py: 0.25 }}>
                  <ListItemIcon sx={{ minWidth: 28 }}><CheckIcon fontSize="small" color="primary" /></ListItemIcon>
                  <ListItemText primary="Player paid cash upfront" primaryTypographyProps={{ variant: 'body2' }} />
                </ListItem>
                <ListItem disablePadding sx={{ py: 0.25 }}>
                  <ListItemIcon sx={{ minWidth: 28 }}><CheckIcon fontSize="small" color="primary" /></ListItemIcon>
                  <ListItemText primary="Player has credits from previous pools" primaryTypographyProps={{ variant: 'body2' }} />
                </ListItem>
                <ListItem disablePadding sx={{ py: 0.25 }}>
                  <ListItemIcon sx={{ minWidth: 28 }}><CheckIcon fontSize="small" color="primary" /></ListItemIcon>
                  <ListItemText primary="Accurate accounting from day one" primaryTypographyProps={{ variant: 'body2' }} />
                </ListItem>
              </List>
            </Grid>
          </Grid>
        </Paper>

        <Alert severity="info" sx={{ mt: 2 }}>
          Offline players appear with a distinct color on the board and are tracked separately in accounting.
        </Alert>
      </Box>

      <Divider sx={{ my: 4 }} />

      {/* Merging Offline Players */}
      <Box id="merge-offline-players" sx={{ mb: 5 }}>
        <Typography variant="h5" gutterBottom fontWeight="bold">
          <MergeIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
          Merging Offline Players to Registered Users
        </Typography>

        <Typography variant="body1" paragraph>
          When an offline player later creates a SquareUp account, you can <strong>merge their squares</strong> into their new registered account. This transfers ownership and keeps their history intact.
        </Typography>

        <Alert severity="warning" sx={{ mb: 3 }}>
          <Typography variant="body2">
            <strong>Approval Required:</strong> The target user must approve the merge request. This ensures players consent to receiving squares (and any associated costs).
          </Typography>
        </Alert>

        <Grid container spacing={3}>
          <Grid size={{ xs: 12, md: 6 }}>
            <Paper elevation={0} sx={{ p: 3, border: 1, borderColor: 'divider', height: '100%' }}>
              <Typography variant="h6" gutterBottom>
                <SendIcon sx={{ verticalAlign: 'middle', mr: 1, fontSize: 20 }} />
                For Managers: Requesting a Merge
              </Typography>
              <List dense>
                <ListItem>
                  <ListItemIcon><CheckIcon color="success" /></ListItemIcon>
                  <ListItemText primary="Go to the board's Players tab" />
                </ListItem>
                <ListItem>
                  <ListItemIcon><CheckIcon color="success" /></ListItemIcon>
                  <ListItemText primary="Find the offline player to merge" />
                </ListItem>
                <ListItem>
                  <ListItemIcon><CheckIcon color="success" /></ListItemIcon>
                  <ListItemText primary="Click 'Merge to User' and select the target" />
                </ListItem>
                <ListItem>
                  <ListItemIcon><CheckIcon color="success" /></ListItemIcon>
                  <ListItemText primary="Wait for the user to approve" />
                </ListItem>
              </List>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                You'll see a pending indicator on the Players tab while awaiting approval.
              </Typography>
            </Paper>
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <Paper elevation={0} sx={{ p: 3, border: 1, borderColor: 'divider', height: '100%' }}>
              <Typography variant="h6" gutterBottom>
                <CheckIcon sx={{ verticalAlign: 'middle', mr: 1, fontSize: 20, color: 'success.main' }} />
                For Players: Responding to Requests
              </Typography>
              <List dense>
                <ListItem>
                  <ListItemIcon><CheckIcon color="success" /></ListItemIcon>
                  <ListItemText primary="You'll receive a notification (check your notification bell or email)" />
                </ListItem>
                <ListItem>
                  <ListItemIcon><CheckIcon color="success" /></ListItemIcon>
                  <ListItemText primary="Click the notification to go to your Connections page" />
                </ListItem>
                <ListItem>
                  <ListItemIcon><CheckIcon color="success" /></ListItemIcon>
                  <ListItemText primary="Review which offline player, boards affected, and how many squares" />
                </ListItem>
                <ListItem>
                  <ListItemIcon><CheckIcon color="success" /></ListItemIcon>
                  <ListItemText primary="Click Approve to accept, or Deny to decline" />
                </ListItem>
              </List>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Requests expire after 7 days if not responded to.</Typography>
            </Paper>
          </Grid>
        </Grid>

        {/* Merge Request Status */}
        <Paper elevation={0} sx={{ p: 3, border: 1, borderColor: 'primary.main', bgcolor: 'primary.50', mt: 3 }}>
          <Typography variant="h6" gutterBottom>Merge Request Status</Typography>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Meaning</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                <TableCell>
                  <Chip label="Pending" size="small" color="warning" />
                </TableCell>
                <TableCell>Waiting for the target user to approve or deny</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>
                  <Chip label="Approved" size="small" color="success" />
                </TableCell>
                <TableCell>User accepted - squares have been transferred</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>
                  <Chip label="Denied" size="small" color="error" />
                </TableCell>
                <TableCell>User declined - offline player remains unchanged</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>
                  <Chip label="Expired" size="small" color="default" />
                </TableCell>
                <TableCell>No response within 7 days - request auto-cancelled</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </Paper>

        <Box sx={{ mt: 3 }}>
        <InfoCard title="What Gets Transferred" variant="tip">
          <List dense disablePadding>
            <ListItem disablePadding sx={{ py: 0.5 }}>
              <ListItemIcon sx={{ minWidth: 28 }}><CheckIcon fontSize="small" color="success" /></ListItemIcon>
              <ListItemText primary="All squares owned by the offline player" primaryTypographyProps={{ variant: 'body2' }} />
            </ListItem>
            <ListItem disablePadding sx={{ py: 0.5 }}>
              <ListItemIcon sx={{ minWidth: 28 }}><CheckIcon fontSize="small" color="success" /></ListItemIcon>
              <ListItemText primary="Starting balance (if any was set)" primaryTypographyProps={{ variant: 'body2' }} />
            </ListItem>
            <ListItem disablePadding sx={{ py: 0.5 }}>
              <ListItemIcon sx={{ minWidth: 28 }}><CheckIcon fontSize="small" color="success" /></ListItemIcon>
              <ListItemText primary="The offline player is removed from the board" primaryTypographyProps={{ variant: 'body2' }} />
            </ListItem>
          </List>
        </InfoCard>
        </Box>

        <Alert severity="info" sx={{ mt: 2 }}>
          <Typography variant="body2">
            <strong>Tip:</strong> Communicate with players before sending merge requests. Let them know to expect the notification and what it means.
          </Typography>
        </Alert>

        {/* Smart Merge Suggestions Section */}
        <Paper elevation={0} sx={{ p: 3, border: 1, borderColor: 'warning.main', bgcolor: 'warning.50', mt: 3 }}>
          <Typography variant="h6" gutterBottom>Smart Merge Suggestions</Typography>
          <Typography variant="body2" paragraph>
            SquareUp automatically detects when a new user might match an offline player. When someone joins your board, we check if their name closely matches any of your offline players across <strong>all boards you manage</strong>.
          </Typography>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 6 }}>
              <Typography variant="subtitle2" fontWeight="bold" gutterBottom>How It Works</Typography>
              <List dense disablePadding>
                <ListItem disablePadding sx={{ py: 0.25 }}>
                  <ListItemIcon sx={{ minWidth: 28 }}><CheckIcon fontSize="small" color="warning" /></ListItemIcon>
                  <ListItemText primary="90% name similarity threshold to avoid false positives" primaryTypographyProps={{ variant: 'body2' }} />
                </ListItem>
                <ListItem disablePadding sx={{ py: 0.25 }}>
                  <ListItemIcon sx={{ minWidth: 28 }}><CheckIcon fontSize="small" color="warning" /></ListItemIcon>
                  <ListItemText primary="You receive a notification suggesting the merge" primaryTypographyProps={{ variant: 'body2' }} />
                </ListItem>
                <ListItem disablePadding sx={{ py: 0.25 }}>
                  <ListItemIcon sx={{ minWidth: 28 }}><CheckIcon fontSize="small" color="warning" /></ListItemIcon>
                  <ListItemText primary="Click to open merge dialog with players pre-selected" primaryTypographyProps={{ variant: 'body2' }} />
                </ListItem>
              </List>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Typography variant="subtitle2" fontWeight="bold" gutterBottom>From Connections Page</Typography>
              <List dense disablePadding>
                <ListItem disablePadding sx={{ py: 0.25 }}>
                  <ListItemIcon sx={{ minWidth: 28 }}><CheckIcon fontSize="small" color="warning" /></ListItemIcon>
                  <ListItemText primary="Click merge icon on any online connection" primaryTypographyProps={{ variant: 'body2' }} />
                </ListItem>
                <ListItem disablePadding sx={{ py: 0.25 }}>
                  <ListItemIcon sx={{ minWidth: 28 }}><CheckIcon fontSize="small" color="warning" /></ListItemIcon>
                  <ListItemText primary="Suggested matches shown at top with similarity %" primaryTypographyProps={{ variant: 'body2' }} />
                </ListItem>
                <ListItem disablePadding sx={{ py: 0.25 }}>
                  <ListItemIcon sx={{ minWidth: 28 }}><CheckIcon fontSize="small" color="warning" /></ListItemIcon>
                  <ListItemText primary="Search through all your offline players" primaryTypographyProps={{ variant: 'body2' }} />
                </ListItem>
              </List>
            </Grid>
          </Grid>
        </Paper>

        {/* Global Merge Section */}
        <Paper elevation={0} sx={{ p: 3, border: 2, borderColor: 'secondary.main', bgcolor: 'secondary.50', mt: 4 }}>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <MergeIcon color="secondary" />
            Global Merge: Update All Boards at Once
          </Typography>
          <Typography variant="body2" paragraph>
            If an offline player appears on multiple boards you manage, you can use <strong>Global Merge</strong> to transfer all their squares across all boards with a single request.
          </Typography>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 6 }}>
              <Typography variant="subtitle2" fontWeight="bold" gutterBottom>When to Use Global Merge</Typography>
              <List dense disablePadding>
                <ListItem disablePadding sx={{ py: 0.25 }}>
                  <ListItemIcon sx={{ minWidth: 28 }}><CheckIcon fontSize="small" color="secondary" /></ListItemIcon>
                  <ListItemText primary="Offline player is on multiple boards" primaryTypographyProps={{ variant: 'body2' }} />
                </ListItem>
                <ListItem disablePadding sx={{ py: 0.25 }}>
                  <ListItemIcon sx={{ minWidth: 28 }}><CheckIcon fontSize="small" color="secondary" /></ListItemIcon>
                  <ListItemText primary="Same person just created an account" primaryTypographyProps={{ variant: 'body2' }} />
                </ListItem>
                <ListItem disablePadding sx={{ py: 0.25 }}>
                  <ListItemIcon sx={{ minWidth: 28 }}><CheckIcon fontSize="small" color="secondary" /></ListItemIcon>
                  <ListItemText primary="Want to clean up everything at once" primaryTypographyProps={{ variant: 'body2' }} />
                </ListItem>
              </List>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Typography variant="subtitle2" fontWeight="bold" gutterBottom>How It Works</Typography>
              <List dense disablePadding>
                <ListItem disablePadding sx={{ py: 0.25 }}>
                  <ListItemIcon sx={{ minWidth: 28 }}><CheckIcon fontSize="small" color="secondary" /></ListItemIcon>
                  <ListItemText primary="Check 'Merge on all boards' when creating request" primaryTypographyProps={{ variant: 'body2' }} />
                </ListItem>
                <ListItem disablePadding sx={{ py: 0.25 }}>
                  <ListItemIcon sx={{ minWidth: 28 }}><CheckIcon fontSize="small" color="secondary" /></ListItemIcon>
                  <ListItemText primary="Target user sees all affected boards" primaryTypographyProps={{ variant: 'body2' }} />
                </ListItem>
                <ListItem disablePadding sx={{ py: 0.25 }}>
                  <ListItemIcon sx={{ minWidth: 28 }}><CheckIcon fontSize="small" color="secondary" /></ListItemIcon>
                  <ListItemText primary="One approval = all boards transfer atomically" primaryTypographyProps={{ variant: 'body2' }} />
                </ListItem>
              </List>
            </Grid>
          </Grid>
          <Alert severity="success" sx={{ mt: 2 }}>
            <Typography variant="body2">
              <strong>Auto-Add:</strong> The target user doesn't need to be on every board! When they approve the merge, they're automatically added as a participant to any boards they're not already on. This ensures complete consolidation with no stragglers.
            </Typography>
          </Alert>
        </Paper>

        {/* Visual Indicators Section */}
        <Paper elevation={0} sx={{ p: 3, border: 1, borderColor: 'info.main', bgcolor: 'info.50', mt: 3 }}>
          <Typography variant="h6" gutterBottom>Visual Indicators on Players Tab</Typography>
          <Typography variant="body2" paragraph>
            Look for these chips on the Players tab to track merge request status:
          </Typography>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Chip label="Merge Pending" color="warning" size="small" icon={<MergeIcon />} />
                <Typography variant="body2">Offline player with pending request</Typography>
              </Box>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Chip label="Merge Request" color="info" size="small" icon={<MergeIcon />} />
                <Typography variant="body2">Registered user who is target of a request</Typography>
              </Box>
            </Grid>
          </Grid>
        </Paper>
      </Box>

      <Divider sx={{ my: 4 }} />

      {/* Number Randomization */}
      <Box id="randomization" sx={{ mb: 5 }}>
        <Typography variant="h5" gutterBottom fontWeight="bold">
          <ShuffleIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
          Number Randomization
        </Typography>

        <Typography variant="body1" paragraph>
          The magic of squares pools is that winning squares are determined by random number assignment. Each team gets the digits 0-9 assigned randomly to their row or column.
        </Typography>

        <VisualExample title="Before vs After Randomization" caption="Numbers are assigned randomly and independently for each team">
          <Grid container spacing={4} justifyContent="center">
            <Grid size="grow">
              <Typography variant="subtitle2" sx={{ mb: 1, textAlign: 'center' }}>Before</Typography>
              <MiniGrid
                rows={3}
                cols={3}
                homeNumbers={[0, 1, 2]}
                awayNumbers={[0, 1, 2]}
                homeTeam="Home"
                awayTeam="Away"
              />
            </Grid>
            <Grid size="grow">
              <Typography variant="subtitle2" sx={{ mb: 1, textAlign: 'center' }}>After</Typography>
              <MiniGrid
                rows={3}
                cols={3}
                homeNumbers={[7, 3, 9]}
                awayNumbers={[2, 8, 5]}
                homeTeam="Home"
                awayTeam="Away"
              />
            </Grid>
          </Grid>
        </VisualExample>

        <Grid container spacing={2} sx={{ mt: 2 }}>
          <Grid size={{ xs: 12, md: 6 }}>
            <Paper elevation={0} sx={{ p: 2, border: 1, borderColor: 'divider' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <VisibilityOffIcon color="action" />
                <Typography variant="subtitle2" fontWeight="bold">Numbers Hidden</Typography>
              </Box>
              <Typography variant="body2" color="text.secondary">
                After randomization, numbers can be hidden until you're ready. This adds suspense—players don't know their numbers until game time!
              </Typography>
            </Paper>
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <Paper elevation={0} sx={{ p: 2, border: 1, borderColor: 'divider' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <VisibilityIcon color="primary" />
                <Typography variant="subtitle2" fontWeight="bold">Reveal Numbers</Typography>
              </Box>
              <Typography variant="body2" color="text.secondary">
                Click "Reveal Numbers" when you're ready. Once revealed, everyone can see which numbers correspond to which squares.
              </Typography>
            </Paper>
          </Grid>
        </Grid>

        <Alert severity="warning" sx={{ mt: 3 }}>
          <strong>One-time action:</strong> You can only randomize numbers once. Make sure all squares are claimed before randomizing!
        </Alert>
      </Box>

      <Divider sx={{ my: 4 }} />

      {/* Board Lock */}
      <Box id="locking" sx={{ mb: 5 }}>
        <Typography variant="h5" gutterBottom fontWeight="bold">
          <LockIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
          Locking Your Board
        </Typography>

        <Typography variant="body1" paragraph>
          Locking your board is a critical step before the game starts. It prevents any changes to square ownership and enables score recording.
        </Typography>

        <Paper elevation={0} sx={{ p: 3, mb: 3, border: 1, borderColor: 'warning.main', bgcolor: 'warning.50' }}>
          <Typography variant="h6" gutterBottom>When to Lock</Typography>
          <List dense>
            <ListItem>
              <ListItemIcon><CheckIcon color="warning" /></ListItemIcon>
              <ListItemText primary="All squares have been claimed (or you've decided to close)" />
            </ListItem>
            <ListItem>
              <ListItemIcon><CheckIcon color="warning" /></ListItemIcon>
              <ListItemText primary="Numbers have been randomized" />
            </ListItem>
            <ListItem>
              <ListItemIcon><CheckIcon color="warning" /></ListItemIcon>
              <ListItemText primary="You're ready for the game to begin" />
            </ListItem>
          </List>
        </Paper>

        <InfoCard title="Unlocking a Board" variant="warning" icon={<LockIcon />}>
          <Typography variant="body2">
            Managers can unlock a board if needed (e.g., to fix an issue), but use this sparingly. It's best to finalize everything before locking.
          </Typography>
        </InfoCard>
      </Box>

      <Divider sx={{ my: 4 }} />

      {/* Multiple Managers */}
      <Box id="managers" sx={{ mb: 5 }}>
        <Typography variant="h5" gutterBottom fontWeight="bold">
          <ManagerIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
          Multiple Managers
        </Typography>

        <Typography variant="body1" paragraph>
          Running a big pool? Add additional managers to help you manage the board. Managers can perform most actions except deleting the board.
        </Typography>

        <Grid container spacing={3}>
          <Grid size={{ xs: 12, md: 6 }}>
            <Paper elevation={0} sx={{ p: 3, border: 1, borderColor: 'divider' }}>
              <Typography variant="h6" gutterBottom color="primary.main">Owner</Typography>
              <List dense>
                <ListItem disablePadding sx={{ py: 0.5 }}>
                  <ListItemIcon sx={{ minWidth: 32 }}><CheckIcon fontSize="small" color="success" /></ListItemIcon>
                  <ListItemText primary="Full control over the board" />
                </ListItem>
                <ListItem disablePadding sx={{ py: 0.5 }}>
                  <ListItemIcon sx={{ minWidth: 32 }}><CheckIcon fontSize="small" color="success" /></ListItemIcon>
                  <ListItemText primary="Add/remove managers" />
                </ListItem>
                <ListItem disablePadding sx={{ py: 0.5 }}>
                  <ListItemIcon sx={{ minWidth: 32 }}><CheckIcon fontSize="small" color="success" /></ListItemIcon>
                  <ListItemText primary="Delete the board" />
                </ListItem>
                <ListItem disablePadding sx={{ py: 0.5 }}>
                  <ListItemIcon sx={{ minWidth: 32 }}><CheckIcon fontSize="small" color="success" /></ListItemIcon>
                  <ListItemText primary="Transfer ownership" />
                </ListItem>
              </List>
            </Paper>
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <Paper elevation={0} sx={{ p: 3, border: 1, borderColor: 'divider' }}>
              <Typography variant="h6" gutterBottom color="secondary.main">Admin (Co-Manager)</Typography>
              <List dense>
                <ListItem disablePadding sx={{ py: 0.5 }}>
                  <ListItemIcon sx={{ minWidth: 32 }}><CheckIcon fontSize="small" color="success" /></ListItemIcon>
                  <ListItemText primary="Manage players and squares" />
                </ListItem>
                <ListItem disablePadding sx={{ py: 0.5 }}>
                  <ListItemIcon sx={{ minWidth: 32 }}><CheckIcon fontSize="small" color="success" /></ListItemIcon>
                  <ListItemText primary="Record scores" />
                </ListItem>
                <ListItem disablePadding sx={{ py: 0.5 }}>
                  <ListItemIcon sx={{ minWidth: 32 }}><CheckIcon fontSize="small" color="success" /></ListItemIcon>
                  <ListItemText primary="Manage payments" />
                </ListItem>
                <ListItem disablePadding sx={{ py: 0.5 }}>
                  <ListItemIcon sx={{ minWidth: 32 }}><CheckIcon fontSize="small" color="success" /></ListItemIcon>
                  <ListItemText primary="Lock/unlock board" />
                </ListItem>
              </List>
            </Paper>
          </Grid>
        </Grid>
      </Box>

      <Divider sx={{ my: 4 }} />

      {/* Board Settings */}
      <Box id="settings" sx={{ mb: 5 }}>
        <Typography variant="h5" gutterBottom fontWeight="bold">
          <EditIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
          Editing Board Settings
        </Typography>

        <Typography variant="body1" paragraph>
          Most board settings can be edited while in <strong>Draft</strong> or <strong>Live</strong> status. Some settings become locked once players have joined or the board is locked.
        </Typography>

        <Paper elevation={0} sx={{ border: 1, borderColor: 'divider', overflow: 'hidden' }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: 'grey.100' }}>
                <TableCell sx={{ fontWeight: 'bold' }}>Setting</TableCell>
                <TableCell sx={{ fontWeight: 'bold', textAlign: 'center' }}>Draft</TableCell>
                <TableCell sx={{ fontWeight: 'bold', textAlign: 'center' }}>Live</TableCell>
                <TableCell sx={{ fontWeight: 'bold', textAlign: 'center' }}>Locked</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {[
                { setting: 'Board Name', draft: true, live: true, locked: true },
                { setting: 'Description', draft: true, live: true, locked: true },
                { setting: 'Team Names', draft: true, live: true, locked: false },
                { setting: 'Team Colors', draft: true, live: true, locked: true },
                { setting: 'Price Per Square', draft: true, live: false, locked: false },
                { setting: 'Payout Config', draft: true, live: true, locked: false },
                { setting: 'Theme', draft: true, live: true, locked: true },
                { setting: 'Home on Top', draft: true, live: true, locked: true },
              ].map((row) => (
                <TableRow key={row.setting}>
                  <TableCell>{row.setting}</TableCell>
                  <TableCell align="center">{row.draft ? <CheckIcon color="success" fontSize="small" /> : '—'}</TableCell>
                  <TableCell align="center">{row.live ? <CheckIcon color="success" fontSize="small" /> : '—'}</TableCell>
                  <TableCell align="center">{row.locked ? <CheckIcon color="success" fontSize="small" /> : '—'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      </Box>

      <Divider sx={{ my: 4 }} />

      {/* Square Requests */}
      <Box id="square-requests" sx={{ mb: 5 }}>
        <Typography variant="h5" gutterBottom fontWeight="bold">
          <RequestIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
          Square Requests
        </Typography>

        <Typography variant="body1" paragraph>
          Square requests help board managers control how many squares each player can claim. When enabled, players must request approval before claiming squares, giving you full control over square distribution.
        </Typography>

        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid size={{ xs: 12, md: 6 }}>
            <Paper elevation={0} sx={{ p: 3, border: 1, borderColor: 'divider', height: '100%' }}>
              <Typography variant="h6" gutterBottom>How It Works</Typography>
              <List dense disablePadding>
                <ListItem disablePadding sx={{ py: 0.5 }}>
                  <ListItemIcon sx={{ minWidth: 32 }}><CheckIcon fontSize="small" color="success" /></ListItemIcon>
                  <ListItemText primary="Players submit a request specifying how many squares they want" />
                </ListItem>
                <ListItem disablePadding sx={{ py: 0.5 }}>
                  <ListItemIcon sx={{ minWidth: 32 }}><CheckIcon fontSize="small" color="success" /></ListItemIcon>
                  <ListItemText primary="Managers review and approve, deny, or modify the requested amount" />
                </ListItem>
                <ListItem disablePadding sx={{ py: 0.5 }}>
                  <ListItemIcon sx={{ minWidth: 32 }}><CheckIcon fontSize="small" color="success" /></ListItemIcon>
                  <ListItemText primary="Once approved, players can claim up to their approved number" />
                </ListItem>
                <ListItem disablePadding sx={{ py: 0.5 }}>
                  <ListItemIcon sx={{ minWidth: 32 }}><CheckIcon fontSize="small" color="success" /></ListItemIcon>
                  <ListItemText primary="Players see their remaining allowance as they claim squares" />
                </ListItem>
              </List>
            </Paper>
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <Paper elevation={0} sx={{ p: 3, border: 1, borderColor: 'divider', height: '100%' }}>
              <Typography variant="h6" gutterBottom>Request Statuses</Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Chip label="Pending" color="warning" size="small" icon={<PendingIcon />} />
                  <Typography variant="body2">Awaiting manager review</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Chip label="Approved" color="success" size="small" icon={<CheckIcon />} />
                  <Typography variant="body2">Player can claim up to the approved number</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Chip label="Denied" color="error" size="small" />
                  <Typography variant="body2">Request was rejected (reason may be provided)</Typography>
                </Box>
              </Box>
            </Paper>
          </Grid>
        </Grid>

        <InfoCard title="Manager Actions" variant="info" icon={<ManagerIcon />}>
          <Typography variant="body2" paragraph>
            As a board manager, you can:
          </Typography>
          <List dense disablePadding>
            <ListItem disablePadding sx={{ py: 0.5 }}>
              <ListItemIcon sx={{ minWidth: 32 }}><CheckIcon fontSize="small" color="success" /></ListItemIcon>
              <ListItemText primary="Approve requests exactly as submitted" />
            </ListItem>
            <ListItem disablePadding sx={{ py: 0.5 }}>
              <ListItemIcon sx={{ minWidth: 32 }}><CheckIcon fontSize="small" color="success" /></ListItemIcon>
              <ListItemText primary="Approve with a different number of squares than requested" />
            </ListItem>
            <ListItem disablePadding sx={{ py: 0.5 }}>
              <ListItemIcon sx={{ minWidth: 32 }}><CheckIcon fontSize="small" color="success" /></ListItemIcon>
              <ListItemText primary="Deny requests with an optional reason" />
            </ListItem>
            <ListItem disablePadding sx={{ py: 0.5 }}>
              <ListItemIcon sx={{ minWidth: 32 }}><CheckIcon fontSize="small" color="success" /></ListItemIcon>
              <ListItemText primary="Filter requests by status to quickly find pending ones" />
            </ListItem>
          </List>
        </InfoCard>

        <Alert severity="info" sx={{ mt: 3 }}>
          <strong>Tip:</strong> If you prefer to manage square assignments yourself, you can skip the request system entirely by using the "Assign Mode" to directly assign squares to players.
        </Alert>
      </Box>

      <Divider sx={{ my: 4 }} />

      {/* Audit Log */}
      <Box id="audit-log" sx={{ mb: 5 }}>
        <Typography variant="h5" gutterBottom fontWeight="bold">
          <AuditIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
          Audit Log
        </Typography>

        <Typography variant="body1" paragraph>
          The audit log provides a complete history of all actions taken on your board. It's invaluable for tracking changes, resolving disputes, and maintaining transparency with players.
        </Typography>

        <Paper elevation={0} sx={{ p: 3, border: 1, borderColor: 'divider', mb: 3 }}>
          <Typography variant="h6" gutterBottom>What Gets Logged</Typography>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Box>
                <Chip label="Board" color="primary" size="small" variant="outlined" sx={{ mb: 1 }} />
                <List dense disablePadding>
                  <ListItem disablePadding sx={{ py: 0.25 }}>
                    <ListItemText primary="Board creation" primaryTypographyProps={{ variant: 'body2' }} />
                  </ListItem>
                  <ListItem disablePadding sx={{ py: 0.25 }}>
                    <ListItemText primary="Settings changes" primaryTypographyProps={{ variant: 'body2' }} />
                  </ListItem>
                  <ListItem disablePadding sx={{ py: 0.25 }}>
                    <ListItemText primary="Status transitions" primaryTypographyProps={{ variant: 'body2' }} />
                  </ListItem>
                  <ListItem disablePadding sx={{ py: 0.25 }}>
                    <ListItemText primary="Number randomization" primaryTypographyProps={{ variant: 'body2' }} />
                  </ListItem>
                </List>
              </Box>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Box>
                <Chip label="Players" color="secondary" size="small" variant="outlined" sx={{ mb: 1 }} />
                <List dense disablePadding>
                  <ListItem disablePadding sx={{ py: 0.25 }}>
                    <ListItemText primary="Player joins/leaves" primaryTypographyProps={{ variant: 'body2' }} />
                  </ListItem>
                  <ListItem disablePadding sx={{ py: 0.25 }}>
                    <ListItemText primary="Manager promotions" primaryTypographyProps={{ variant: 'body2' }} />
                  </ListItem>
                  <ListItem disablePadding sx={{ py: 0.25 }}>
                    <ListItemText primary="Offline player changes" primaryTypographyProps={{ variant: 'body2' }} />
                  </ListItem>
                </List>
              </Box>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Box>
                <Chip label="Squares" color="success" size="small" variant="outlined" sx={{ mb: 1 }} />
                <List dense disablePadding>
                  <ListItem disablePadding sx={{ py: 0.25 }}>
                    <ListItemText primary="Square claims" primaryTypographyProps={{ variant: 'body2' }} />
                  </ListItem>
                  <ListItem disablePadding sx={{ py: 0.25 }}>
                    <ListItemText primary="Assignments" primaryTypographyProps={{ variant: 'body2' }} />
                  </ListItem>
                  <ListItem disablePadding sx={{ py: 0.25 }}>
                    <ListItemText primary="Unclaims/releases" primaryTypographyProps={{ variant: 'body2' }} />
                  </ListItem>
                </List>
              </Box>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Box>
                <Chip label="Scores" color="warning" size="small" variant="outlined" sx={{ mb: 1 }} />
                <List dense disablePadding>
                  <ListItem disablePadding sx={{ py: 0.25 }}>
                    <ListItemText primary="Score recording" primaryTypographyProps={{ variant: 'body2' }} />
                  </ListItem>
                  <ListItem disablePadding sx={{ py: 0.25 }}>
                    <ListItemText primary="Score corrections" primaryTypographyProps={{ variant: 'body2' }} />
                  </ListItem>
                </List>
              </Box>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Box>
                <Chip label="Payments" color="info" size="small" variant="outlined" sx={{ mb: 1 }} />
                <List dense disablePadding>
                  <ListItem disablePadding sx={{ py: 0.25 }}>
                    <ListItemText primary="Payment recording" primaryTypographyProps={{ variant: 'body2' }} />
                  </ListItem>
                  <ListItem disablePadding sx={{ py: 0.25 }}>
                    <ListItemText primary="Winnings payouts" primaryTypographyProps={{ variant: 'body2' }} />
                  </ListItem>
                </List>
              </Box>
            </Grid>
          </Grid>
        </Paper>

        <InfoCard title="Using the Audit Log" variant="info" icon={<AuditIcon />}>
          <Typography variant="body2" paragraph>
            The audit log is visible to all board participants, promoting transparency:
          </Typography>
          <List dense disablePadding>
            <ListItem disablePadding sx={{ py: 0.5 }}>
              <ListItemIcon sx={{ minWidth: 32 }}><CheckIcon fontSize="small" color="success" /></ListItemIcon>
              <ListItemText primary="Filter by category (board, players, squares, scores, payments)" />
            </ListItem>
            <ListItem disablePadding sx={{ py: 0.5 }}>
              <ListItemIcon sx={{ minWidth: 32 }}><CheckIcon fontSize="small" color="success" /></ListItemIcon>
              <ListItemText primary="Filter by specific action type" />
            </ListItem>
            <ListItem disablePadding sx={{ py: 0.5 }}>
              <ListItemIcon sx={{ minWidth: 32 }}><CheckIcon fontSize="small" color="success" /></ListItemIcon>
              <ListItemText primary="See who performed each action and when" />
            </ListItem>
            <ListItem disablePadding sx={{ py: 0.5 }}>
              <ListItemIcon sx={{ minWidth: 32 }}><CheckIcon fontSize="small" color="success" /></ListItemIcon>
              <ListItemText primary="Repeated actions are grouped for easier reading" />
            </ListItem>
          </List>
        </InfoCard>

        <Alert severity="success" sx={{ mt: 3 }}>
          <strong>Transparency:</strong> Players can view the audit log on any board they're participating in, so they can verify that everything is fair and above board.
        </Alert>
      </Box>
    </SupportLayout>
  );
};

export default BoardManagementPage;
